import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { SERVICE_POINT_KINDS, type ServicePointKind, type ServicePointView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import {
  adminServicePointsQuery,
  createServicePoint,
  updateServicePoint,
} from '../api/settings.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../patients/patients.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

export const KIND_LABELS: Record<ServicePointKind, string> = {
  triage: 'Triage',
  consultation: 'Consultation',
  lab: 'Lab',
  pharmacy: 'Pharmacy',
  cashier: 'Cashier',
  other: 'Other',
  casualty: 'Casualty',
};

const kindOptions = SERVICE_POINT_KINDS.map((k) => ({ id: k, label: KIND_LABELS[k] }));

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function AddServicePoint({ onDone, nextOrder }: { onDone: () => void; nextOrder: number }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ServicePointKind | null>(null);
  const [tried, setTried] = useState(false);
  const add = useMutation({
    mutationFn: () =>
      createServicePoint({ name: name.trim(), kind: kind ?? 'other', sortOrder: nextOrder }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: adminServicePointsQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ['service-points'] });
      toasts.show({ title: 'Service point added', description: created.name });
      onDone();
    },
  });
  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (name.trim() !== '' && kind !== null) add.mutate();
      }}
    >
      <div className={styles.row}>
        <TextField
          label="Name"
          description="As patients and staff know it, like Consultation room 2."
          value={name}
          onChange={setName}
          {...(tried && name.trim() === '' ? { errorMessage: 'Enter a name.' } : {})}
        />
        <Select
          label="Kind"
          placeholder="Choose a kind"
          options={kindOptions}
          value={kind}
          onChange={(v) => {
            setKind(v as ServicePointKind);
          }}
          {...(tried && kind === null ? { errorMessage: 'Choose what kind of place it is.' } : {})}
        />
      </div>
      {add.error === null ? null : (
        <Notice tone="error">{message(add.error, "It wasn't added. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={add.isPending} pendingLabel="Adding…">
          Add service point
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function RenameServicePoint({ point, onDone }: { point: ServicePointView; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(point.name);
  const rename = useMutation({
    mutationFn: () => updateServicePoint(point.id, { name: name.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adminServicePointsQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ['service-points'] });
      onDone();
    },
  });
  return (
    <form
      className={styles.inlineForm}
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() !== '') rename.mutate();
      }}
    >
      <TextField label={`New name for ${point.name}`} value={name} onChange={setName} autoFocus />
      {rename.error === null ? null : (
        <Notice tone="error">{message(rename.error, "It wasn't renamed. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" isPending={rename.isPending} pendingLabel="Saving…">
          Rename
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Settings > Service points (plan §6.6): the queues patients are checked in to at this
 * facility. Deactivating hides one from check-in; nothing is deleted.
 */
export function ServicePointsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'admin.facilities.manage');
  const online = useOnline();
  const queryClient = useQueryClient();
  const points = useQuery({ ...adminServicePointsQuery, enabled: allowed });
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<ServicePointView | null>(null);
  const toggle = useMutation({
    mutationFn: (point: ServicePointView) =>
      updateServicePoint(point.id, {
        status: point.status === 'active' ? 'inactive' : 'active',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: adminServicePointsQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ['service-points'] });
    },
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title="Service points" />
        <Notice tone="info">
          You don't have permission to change facility settings. Ask an administrator if you need
          it.
        </Notice>
      </>
    );
  }

  const items = points.data ?? [];
  return (
    <SettingsPage
      title="Service points"
      panel
      intro={`The queues at ${session.facility.name}. Reception checks patients in to one of the active ones.`}
      {...(adding
        ? {}
        : {
            actions: (
              <Button
                isDisabled={!online}
                onPress={() => {
                  setAdding(true);
                }}
              >
                Add service point
              </Button>
            ),
          })}
    >
      {adding ? (
        <AddServicePoint
          nextOrder={items.reduce((max, p) => Math.max(max, p.sortOrder), 0) + 1}
          onDone={() => {
            setAdding(false);
          }}
        />
      ) : null}
      {renaming === null ? null : (
        <RenameServicePoint
          point={renaming}
          onDone={() => {
            setRenaming(null);
          }}
        />
      )}
      {points.isPending ? <LoadingBlock label="Loading service points" rows={3} /> : null}
      {points.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void points.refetch()}>
              Try again
            </Button>
          }
        >
          {message(points.error, "Service points couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {toggle.error === null ? null : (
        <Notice tone="error">
          {message(toggle.error, "That change wasn't saved. Try again.")}
        </Notice>
      )}
      {points.isSuccess ? (
        <Table flat aria-label="Service points">
          <TableHeader>
            <Column isRowHeader>Name</Column>
            <Column>Kind</Column>
            <Column>Status</Column>
            <Column>Actions</Column>
          </TableHeader>
          <TableBody
            items={items}
            renderEmptyState={() =>
              'No service points yet. Add one so Reception can check patients in.'
            }
          >
            {(point) => (
              <Row id={point.id}>
                <Cell>{point.name}</Cell>
                <Cell>{KIND_LABELS[point.kind]}</Cell>
                <Cell>
                  <Tag state={point.status === 'active' ? 'active' : 'neutral'}>
                    {point.status === 'active' ? 'Active' : 'Inactive'}
                  </Tag>
                </Cell>
                <Cell>
                  <span className={styles.actions}>
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      aria-label={`Rename ${point.name}`}
                      onPress={() => {
                        setRenaming(point);
                      }}
                    >
                      Rename
                    </Button>
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online || toggle.isPending}
                      aria-label={`${point.status === 'active' ? 'Deactivate' : 'Activate'} ${point.name}`}
                      onPress={() => {
                        toggle.mutate(point);
                      }}
                    >
                      {point.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </span>
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
      ) : null}
    </SettingsPage>
  );
}
